// Shared look for the dashboard charts: one palette, quiet axes, thin marks.
import { formatCurrencyCompact } from '../../utils/dataUtils';

// Categorical colours in a fixed order (lime first, as in docs/enhance-ui.md,
// then re-ordered and checked with the palette validator). The order is what keeps neighbouring
// colours distinguishable (including for colour-blind readers), so do not
// reorder or extend it: fold extra categories into "Other" instead.
// The dark list opens with the dark theme's lime, yellow and teal (docs/enhance-ui.md),
// stepped into the lightness band for the dark surface and re-ordered so that
// neighbours stay apart (checked with the palette validator).
export const SERIES_COLORS = [
  '#6a9a12', // lime
  '#2a78d6', // blue
  '#d19a00', // yellow
  '#e34948', // red
  '#139a8c', // teal
  '#eb6834', // orange
  '#4a3aa7', // violet
  '#e87ba4'  // magenta
];

export const SERIES_COLORS_DARK = [
  '#78a424', // lime
  '#3987e5', // blue
  '#b38e00', // yellow
  '#e66767', // red
  '#1fa597', // teal
  '#d95926', // orange
  '#9085e9', // violet
  '#d55181'  // magenta
];

const THEMES = {
  light: {
    series: SERIES_COLORS,
    // "Other", "Unassigned" and anything that is not a category of its own
    neutral: '#b8bcc4',
    primaryHover: '#557d0c',
    discount: '#139a8c',
    wash: 'rgba(106, 154, 18, 0.12)',
    washFaint: 'rgba(106, 154, 18, 0.05)',
    area: [
      [0, 'rgba(209, 154, 0, 0.26)'],
      [0.55, 'rgba(106, 154, 18, 0.10)'],
      [1, 'rgba(106, 154, 18, 0)']
    ],
    ink: {
      surface: '#ffffff',
      text: '#4a4d45',
      muted: '#6b6e65',
      grid: '#eceee6',
      baseline: '#d2d5c8'
    },
    tooltip: { background: '#141414', title: '#ffffff', body: '#e5e5e0', border: 'rgba(0, 0, 0, 0)' }
  },
  // The dark theme's own accents (docs/enhance-ui.md) are for marks that
  // show a single measure: a lime line and yellow bars. Charts that tell
  // categories apart keep the eight series colours above.
  dark: {
    series: SERIES_COLORS_DARK,
    neutral: '#4d4d4d',
    primary: '#c6eb72',
    primaryHover: '#d6f291',
    bar: '#e8d94c',
    barHover: '#f1e67a',
    discount: '#4dd9c6',
    wash: 'rgba(198, 235, 114, 0.14)',
    washFaint: 'rgba(198, 235, 114, 0.05)',
    // Area under a line, from the top of the plot down to the baseline
    area: [
      [0, 'rgba(232, 217, 76, 0.34)'],
      [0.55, 'rgba(198, 235, 114, 0.10)'],
      [1, 'rgba(198, 235, 114, 0)']
    ],
    ink: {
      surface: '#1b1b1b',
      text: '#b8b8b8',
      muted: '#8c8c8c',
      grid: '#262626',
      baseline: '#3a3a3a'
    },
    tooltip: { background: '#0b0b0b', title: '#ffffff', body: '#d0d0d0', border: '#2a2a2a' }
  }
};

// Colours and inks for the given theme ("light" or "dark").
export const getChartTheme = (themeName) => {
  const theme = THEMES[themeName === 'dark' ? 'dark' : 'light'];
  const primary = theme.primary || theme.series[0];
  return {
    ...theme,
    // A single line
    primary,
    // Bars of a single measure
    bar: theme.bar || primary,
    barHover: theme.barHover || theme.primaryHover,
    // Rows that reduce spend (discounts, credits): teal, apart from the bars
    discount: theme.discount || theme.series[2]
  };
};

// Fill under a line: a flat wash, or where the theme defines one a gradient
// that fades towards the baseline.
export const areaFill = (theme) => {
  if (!theme.area) return theme.wash;
  return (context) => {
    const chart = context && context.chart;
    // The plot area is not known until the chart has been laid out once
    if (!chart || !chart.chartArea) return theme.wash;
    const gradient = chart.ctx.createLinearGradient(0, chart.chartArea.top, 0, chart.chartArea.bottom);
    theme.area.forEach(([stop, color]) => gradient.addColorStop(stop, color));
    return gradient;
  };
};

const FONT_FAMILY = "'Plus Jakarta Sans', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif";

// Colours for the names shown together in one chart. A name keeps the colour
// it had before, so changing the time range never repaints it; a new name
// takes the first colour that none of the names beside it is using.
const assignedSlots = new Map();

export const getSeriesColors = (names, themeName) => {
  const { series, neutral } = getChartTheme(themeName);
  const used = new Set();
  const slots = names.map((name) => {
    const slot = assignedSlots.get(name);
    if (slot === undefined || used.has(slot)) return null;
    used.add(slot);
    return slot;
  });

  return names.map((name, index) => {
    let slot = slots[index];
    if (slot === null) {
      slot = series.findIndex((_, candidate) => !used.has(candidate));
      // More names than colours: the rest share the neutral colour
      if (slot === -1) return neutral;
      used.add(slot);
      assignedSlots.set(name, slot);
    }
    return series[slot];
  });
};

export const truncateLabel = (label, maxLength) => {
  const text = String(label);
  return text.length > maxLength ? text.substring(0, maxLength - 3) + '...' : text;
};

// "2026-09" -> "Sep 2026". Anything else is shown as it is.
export const formatPeriodLabel = (period) => {
  const match = /^(\d{4})-(\d{2})$/.exec(String(period));
  if (!match) return String(period);
  const date = new Date(Number(match[1]), Number(match[2]) - 1, 1);
  return date.toLocaleString('en-US', { month: 'short', year: 'numeric' });
};

export const tickFont = { family: FONT_FAMILY, size: 12 };

export const tooltipStyle = (theme) => ({
  backgroundColor: theme.tooltip.background,
  titleColor: theme.tooltip.title,
  bodyColor: theme.tooltip.body,
  borderColor: theme.tooltip.border,
  borderWidth: 1,
  titleFont: { family: FONT_FAMILY, size: 12, weight: '600' },
  bodyFont: { family: FONT_FAMILY, size: 12 },
  padding: 10,
  cornerRadius: 8,
  displayColors: false
});

// Hairline value axis with compact currency ticks
export const valueAxis = (theme) => ({
  beginAtZero: true,
  border: { display: false },
  grid: { color: theme.ink.grid, drawTicks: false },
  ticks: {
    color: theme.ink.muted,
    font: tickFont,
    padding: 8,
    maxTicksLimit: 6,
    callback: value => formatCurrencyCompact(value)
  }
});

// Value axis for bar charts whose bars carry their own value labels: no
// ticks or grid, only a baseline at zero.
export const barValueAxis = (theme) => ({
  beginAtZero: true,
  border: { display: false },
  ticks: { display: false },
  grid: {
    drawTicks: false,
    color: context => (context.tick && context.tick.value === 0 ? theme.ink.baseline : 'transparent')
  }
});

// Category axis for horizontal bars: names only, no grid
export const categoryAxis = (maxLabelLength, theme) => ({
  border: { display: false },
  grid: { display: false },
  ticks: {
    color: theme.ink.text,
    font: tickFont,
    padding: 6,
    callback: function(value) {
      return truncateLabel(this.getLabelForValue(value), maxLabelLength);
    }
  }
});

// Thin bars with a rounded data end and a square baseline
export const barDataset = (label, values, colors) => ({
  label,
  data: values,
  backgroundColor: colors,
  hoverBackgroundColor: colors,
  borderWidth: 0,
  borderRadius: 4,
  borderSkipped: 'start',
  maxBarThickness: 22,
  minBarLength: 3
});

// Height that gives each horizontal bar its own row instead of stretching a
// few bars over a fixed box.
export const barChartHeight = (barCount) => Math.max(120, barCount * 34 + 20);

// Writes each bar's value at its tip, so amounts can be read without hovering.
export const barValueLabels = (theme) => ({
  id: 'barValueLabels',
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const zeroX = chart.scales.x.getPixelForValue(0);
    const values = chart.data.datasets[0].data;
    ctx.save();
    ctx.font = `500 12px ${FONT_FAMILY}`;
    ctx.fillStyle = theme.ink.text;
    ctx.textBaseline = 'middle';
    meta.data.forEach((bar, index) => {
      const value = values[index];
      const text = formatCurrencyCompact(value);
      // A negative bar grows to the left, so its label sits in the free
      // space on the other side of the baseline
      ctx.textAlign = 'left';
      ctx.fillText(text, (value < 0 ? zeroX : bar.x) + 6, bar.y);
    });
    ctx.restore();
  }
});

// Room on the right for the value labels
export const BAR_LABEL_PADDING = { right: 64, left: 0, top: 4, bottom: 0 };

export const ChartEmptyState = ({ children }) => (
  <div className="chart-empty">{children}</div>
);
